import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiOutlineHome,
  HiOutlineUsers,
  HiOutlineCalendar,
  HiOutlineDocumentText,
  HiOutlineUserGroup,
  HiOutlinePhotograph,
  HiOutlineInformationCircle,
  HiOutlineAcademicCap,
  HiOutlineLogout,
  HiMenu,
  HiX,
  HiChevronLeft,
  HiChevronRight,
  HiAnnotation,
  HiOutlineBell,
  HiOutlineCog,
  HiOutlineQuestionMarkCircle
} from 'react-icons/hi';
import Cookies from "js-cookie";

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [hoveredItem, setHoveredItem] = useState(null);
  const location = useLocation();

  // Save collapse preference
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsCollapsed(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key === 'b' && !isMobile) {
        e.preventDefault();
        setIsCollapsed(!isCollapsed);
      }
      
      if (e.altKey && !isNaN(parseInt(e.key)) && !isMobile) {
        e.preventDefault();
        const index = parseInt(e.key) - 1;
        if (menuItems[index]) {
          window.location.href = menuItems[index].path;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCollapsed, isMobile]);

  const menuItems = [
    { path: '/home', icon: HiOutlineHome, label: 'Home', shortcut: '1' },
    { path: '/manage-user', icon: HiOutlineUsers, label: 'Manage User', shortcut: '2' },
    { path: '/events', icon: HiOutlineCalendar, label: 'Events', shortcut: '3' },
    { path: '/notice', icon: HiOutlineDocumentText, label: 'Notice', shortcut: '4' },
    { path: '/committee', icon: HiOutlineUserGroup, label: 'Committee', shortcut: '5' },
    { path: '/blog', icon: HiOutlineDocumentText, label: 'Blog', shortcut: '6' },
    { path: '/gallery', icon: HiOutlinePhotograph, label: 'Gallery', shortcut: '7' },
    { path: '/about-us', icon: HiOutlineInformationCircle, label: 'About Us', shortcut: '8' },
    { path: '/courses', icon: HiOutlineAcademicCap, label: 'Course', shortcut: '9' },
    { path: '/posts', icon: HiAnnotation, label: 'Posts', shortcut: '0' },
  ];

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      Cookies.remove("authToken");
      Cookies.remove("tokenTimestamp");
      window.location.href = '/login';
    }
  };

  const getActiveItem = () => {
    return menuItems.find(item => item.path === location.pathname)?.label || '';
  };

  // Tooltip component for collapsed mode
  const Tooltip = ({ children, label, shortcut }) => (
    <div className="relative group">
      {children}
      {isCollapsed && (
        <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
          {label}
          {shortcut && <span className="ml-2 text-gray-400">Alt+{shortcut}</span>}
        </div>
      )}
    </div>
  );

  // Mobile sidebar with overlay
  if (isMobile) {
    return (
      <>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={toggleSidebar}
          className="fixed top-3 left-3 z-50 p-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg shadow-lg"
          aria-label="Toggle menu"
          aria-expanded={isOpen}
        >
          <HiMenu className="w-5 h-5" />
        </motion.button>

        <AnimatePresence>
          {isOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.5 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={toggleSidebar}
                className="fixed inset-0 bg-black backdrop-blur-sm z-40"
                aria-hidden="true"
              />
              
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="fixed left-0 top-0 h-full w-64 bg-gradient-to-b from-gray-800 to-gray-900 shadow-2xl z-50 overflow-y-auto"
                role="dialog"
                aria-modal="true"
                aria-label="Navigation menu"
              >
                <div className="p-4 border-b border-gray-700">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-white text-sm font-bold">Admin Portal</h2>
                        <p className="text-gray-400 text-xs">v2.0.0</p>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={toggleSidebar}
                      className="text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors"
                      aria-label="Close menu"
                    >
                      <HiX className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>

                <nav className="p-3">
                  {menuItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={toggleSidebar}
                      className={({ isActive }) =>
                        `flex items-center px-3 py-2.5 text-white rounded-lg transition-all mb-1 ${
                          isActive
                            ? 'bg-gray-700 shadow-lg'
                            : 'hover:bg-gray-700/50'
                        }`
                      }
                    >
                      <item.icon className="w-4 h-4 mr-3" />
                      <span className="text-xs">{item.label}</span>
                    </NavLink>
                  ))}
                </nav>

                <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-700 bg-gradient-to-b from-gray-800 to-gray-900">
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={handleLogout}
                    className="w-full flex items-center px-3 py-2 text-white hover:bg-red-500/20 rounded-lg transition-all"
                  >
                    <HiOutlineLogout className="w-4 h-4 mr-3 text-red-400" />
                    <span className="text-xs">Logout</span>
                  </motion.button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </>
    );
  }

  // Desktop sidebar - FIXED position
  return (
    <>
      {/* Fixed sidebar that doesn't scroll with page */}
      <motion.div
        animate={{ width: isCollapsed ? 72 : 240 }}
        transition={{ type: 'spring', damping: 20, stiffness: 200 }}
        className="fixed left-0 top-0 bottom-0 bg-gradient-to-b from-gray-800 to-gray-900 shadow-2xl z-30 overflow-hidden"
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Collapse toggle button */}
        <Tooltip label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} shortcut="Alt+B">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="absolute -right-3 top-5 bg-gray-700 text-white p-2 rounded-full shadow-lg hover:bg-gray-600 z-20 transition-colors"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <HiChevronRight className="w-3 h-3" />
            ) : (
              <HiChevronLeft className="w-3 h-3" />
            )}
          </motion.button>
        </Tooltip>

        {/* Sidebar content - full height, no scrolling needed */}
        <div className="h-full flex flex-col">
          {/* Header - Fixed at top */}
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center space-x-3">
              <motion.div
                whileHover={{ rotate: 10 }}
                className="bg-gray-700 p-2 rounded-lg flex-shrink-0"
              >
                <svg
                  className="w-5 h-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </motion.div>
              
              <AnimatePresence>
                {!isCollapsed && (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <h2 className="text-white text-sm font-bold">Admin Portal</h2>
                    <p className="text-gray-400 text-xs mt-0.5">v2.0.0</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Navigation - Takes remaining space, no scroll needed if content fits */}
          <div className="flex-1 overflow-y-auto py-2 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent">
            <nav className="px-3 space-y-1">
              {menuItems.map((item) => (
                <Tooltip key={item.path} label={item.label} shortcut={item.shortcut}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center px-3 py-2.5 text-white rounded-lg transition-all group ${
                        isActive
                          ? 'bg-gray-700 shadow-lg'
                          : 'hover:bg-gray-700/50'
                      } ${isCollapsed ? 'justify-center' : ''}`
                    }
                    onMouseEnter={() => setHoveredItem(item.path)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <item.icon className={`w-5 h-5 ${!isCollapsed && 'mr-3'} transition-transform group-hover:scale-110`} />
                    
                    {!isCollapsed && (
                      <>
                        <span className="text-xs flex-1">{item.label}</span>
                        {location.pathname === item.path && (
                          <motion.div
                            layoutId="activeIndicator"
                            className="w-1.5 h-1.5 bg-white rounded-full"
                          />
                        )}
                      </>
                    )}
                  </NavLink>
                </Tooltip>
              ))}
            </nav>
          </div>

          {/* Logout button - Fixed at bottom */}
          <div className="border-t border-gray-700 p-3">
            <Tooltip label="Logout">
              <motion.button
                whileHover={{ x: isCollapsed ? 0 : 5 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleLogout}
                className={`w-full flex items-center px-3 py-2 text-white hover:bg-red-500/20 rounded-lg transition-all ${
                  isCollapsed ? 'justify-center' : ''
                }`}
              >
                <HiOutlineLogout className={`w-4 h-4 ${!isCollapsed && 'mr-3'} text-red-400`} />
                {!isCollapsed && <span className="text-xs">Logout</span>}
              </motion.button>
            </Tooltip>
            
            {/* Version info - only when expanded */}
            {!isCollapsed && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-2 text-center"
              >
                <p className="text-xs text-gray-500">© 2024 Admin Portal</p>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Spacer div to push main content - adjusts based on sidebar width */}
      <div 
        style={{ width: isCollapsed ? 72 : 240 }} 
        className="flex-shrink-0 transition-all duration-300"
      />
    </>
  );
};

export default Sidebar;